import type { Position, RoleBiases, RoleConstraints, TacticalRole } from '../../types';
import type { ActionType, PassType } from './ActionCandidate';
import type { EngineTechniqueType } from '../data/EngineData';

export interface RoleDefinition {
  id: TacticalRole;
  allowedPositions: Position[];
  actionBiases: RoleBiases;
  movementBiases: RoleBiases;
  constraints: RoleConstraints;
}

const constraints = (
  allowedPositions: Position[],
  homeZoneBias: string[],
  allowedZones: string[],
  maxAdvance: number,
  maxDrop: number,
  canEnterBox: boolean,
  canPressHigh: boolean,
  canRoam: boolean,
  recoveryPriority: number,
): RoleConstraints => ({
  allowedPositions,
  homeZoneBias,
  allowedZones,
  maxAdvance,
  maxDrop,
  canEnterBox,
  canPressHigh,
  canRoam,
  emergencyOverride: true,
  recoveryPriority,
});

/** Centralized balance table: no role multiplier is hidden in a resolver. */
export const ROLE_DEFINITIONS: Record<TacticalRole, RoleDefinition> = {
  CENTRAL: {
    id: 'CENTRAL', allowedPositions: ['DF'],
    actionBiases: { tackleBias: 1.10, interceptBias: 1.10, blockBias: 1.15, clearBallBias: 1.10, shootBias: .35 },
    movementBiases: { holdShapeBias: 1.20, forwardRunBias: .55, recoveryBias: 1.12, markBias: 1.10 },
    constraints: constraints(['DF'], ['DEFENSIVE', 'CENTRAL'], ['DEFENSIVE', 'MIDDLE'], 60, 4, false, false, false, 1.12),
  },
  LATERAL: {
    id: 'LATERAL', allowedPositions: ['DF'],
    actionBiases: { pressBias: 1.08, passBias: 1.05, widePassBias: 1.12, shootBias: .45 },
    movementBiases: { supportBias: 1.08, forwardRunBias: 1.05, recoveryBias: 1.15, widePositionBias: 1.16 },
    constraints: constraints(['DF'], ['DEFENSIVE', 'WIDE'], ['DEFENSIVE', 'MIDDLE', 'FINAL_WIDE'], 82, 4, false, true, false, 1.15),
  },
  LIBERO: {
    id: 'LIBERO', allowedPositions: ['DF'],
    actionBiases: { interceptBias: 1.18, passBias: 1.08, tackleBias: .95, shootBias: .35 },
    movementBiases: { supportBias: 1.08, holdShapeBias: 1.05, recoveryBias: 1.20, forwardRunBias: .75 },
    constraints: constraints(['DF'], ['DEFENSIVE', 'CENTRAL'], ['DEFENSIVE', 'MIDDLE'], 66, 3, false, false, true, 1.20),
  },
  ORGANIZER: {
    id: 'ORGANIZER', allowedPositions: ['MF'],
    actionBiases: { passBias: 1.20, dribbleBias: .90, shootBias: .75, riskTolerance: .95 },
    movementBiases: { supportBias: 1.18, holdShapeBias: 1.05, recoveryBias: 1.02 },
    constraints: constraints(['MF'], ['MIDDLE', 'CENTRAL'], ['DEFENSIVE', 'MIDDLE', 'FINAL'], 82, 17, true, false, true, 1.02),
  },
  BALL_WINNER: {
    id: 'BALL_WINNER', allowedPositions: ['MF'],
    actionBiases: { tackleBias: 1.18, interceptBias: 1.18, pressBias: 1.15, markBias: 1.10, passBias: .95, shootBias: .60 },
    movementBiases: { forwardRunBias: .75, recoveryBias: 1.16, pressBias: 1.15, holdShapeBias: 1.08 },
    constraints: constraints(['MF'], ['DEFENSIVE', 'MIDDLE', 'CENTRAL'], ['DEFENSIVE', 'MIDDLE'], 75, 10, false, true, true, 1.16),
  },
  INTERIOR: {
    id: 'INTERIOR', allowedPositions: ['MF'],
    actionBiases: { passBias: 1.08, dribbleBias: 1, shootBias: .85, pressBias: 1.05 },
    movementBiases: { supportBias: 1.12, forwardRunBias: 1.05, recoveryBias: 1.08 },
    constraints: constraints(['MF'], ['MIDDLE', 'HALF_SPACE'], ['DEFENSIVE', 'MIDDLE', 'FINAL'], 87, 15, true, true, true, 1.08),
  },
  ATTACKING_MIDFIELDER: {
    id: 'ATTACKING_MIDFIELDER', allowedPositions: ['MF'],
    actionBiases: { passBias: 1.12, dribbleBias: 1.08, throughPassBias: 1.18, shootBias: 1 },
    movementBiases: { supportBias: 1.10, forwardRunBias: 1.05, recoveryBias: .75 },
    constraints: constraints(['MF'], ['MIDDLE', 'FINAL', 'CENTRAL'], ['MIDDLE', 'FINAL'], 94, 27, true, true, true, .75),
  },
  FINISHER: {
    id: 'FINISHER', allowedPositions: ['FW'],
    actionBiases: { shootBias: 1.25, techniqueBias: 1.15, passBias: .80 },
    movementBiases: { forwardRunBias: 1.12, boxPresenceBias: 1.20, recoveryBias: .65 },
    constraints: constraints(['FW'], ['FINAL', 'CENTRAL', 'BOX'], ['MIDDLE', 'FINAL'], 98, 54, true, true, false, .65),
  },
  WINGER: {
    id: 'WINGER', allowedPositions: ['FW'],
    actionBiases: { dribbleBias: 1.15, passBias: 1.05, pressBias: 1.05, shootBias: .95, widePassBias: 1.12 },
    movementBiases: { forwardRunBias: 1.15, widePositionBias: 1.20, recoveryBias: .75 },
    constraints: constraints(['FW'], ['MIDDLE', 'FINAL', 'WIDE'], ['MIDDLE', 'FINAL', 'FINAL_WIDE'], 98, 45, true, true, true, .75),
  },
  SECOND_STRIKER: {
    id: 'SECOND_STRIKER', allowedPositions: ['FW'],
    actionBiases: { passBias: 1.10, dribbleBias: 1.05, shootBias: 1.05 },
    movementBiases: { supportBias: 1.18, forwardRunBias: 1, recoveryBias: .80 },
    constraints: constraints(['FW'], ['MIDDLE', 'FINAL', 'CENTRAL'], ['MIDDLE', 'FINAL'], 96, 40, true, true, true, .80),
  },
};

const VALID_ROLES: Record<Position, readonly TacticalRole[]> = {
  GK: [],
  DF: ['CENTRAL', 'LATERAL', 'LIBERO'],
  MF: ['ORGANIZER', 'BALL_WINNER', 'INTERIOR', 'ATTACKING_MIDFIELDER'],
  FW: ['FINISHER', 'WINGER', 'SECOND_STRIKER'],
};

/** Roles that a manager can manually assign to a player of this position. */
export function tacticalRolesForPosition(position: Position): readonly TacticalRole[] {
  return VALID_ROLES[position];
}

export function validateAssignedRole(position: Position, role: TacticalRole | null): boolean {
  return position === 'GK' ? role === null : role !== null && VALID_ROLES[position].includes(role);
}

export function getDefaultRoleForPosition(position: Position): TacticalRole | null {
  if (position === 'GK') return null;
  if (position === 'DF') return 'CENTRAL';
  if (position === 'MF') return 'ORGANIZER';
  return 'FINISHER';
}

export function validateRoleForPlayer(
  player: { position?: Position; positionType?: Position },
  role: TacticalRole | null,
): boolean {
  const position = player.position ?? player.positionType;
  return position !== undefined && validateAssignedRole(position, role);
}

export function normaliseAssignedRole(position: Position, role: TacticalRole | null | undefined): TacticalRole | null {
  return role !== undefined && validateAssignedRole(position, role) ? role : getDefaultRoleForPosition(position);
}

/** Converts the formation slot into a safe tactical default without exposing a GK role. */
export function tacticalRoleForFormationSlot(position: Position, formationRole?: string): TacticalRole | null {
  if (position === 'GK') return null;
  const slot = formationRole?.toUpperCase();
  if (position === 'DF') {
    if (slot === 'LB' || slot === 'RB' || slot === 'LWB' || slot === 'RWB') return 'LATERAL';
    if (slot === 'SW' || slot === 'LIBERO') return 'LIBERO';
    return 'CENTRAL';
  }
  if (position === 'MF') {
    if (slot === 'DM') return 'BALL_WINNER';
    if (slot === 'AM') return 'ATTACKING_MIDFIELDER';
    if (slot === 'LM' || slot === 'RM') return 'INTERIOR';
    return 'ORGANIZER';
  }
  if (slot === 'LW' || slot === 'RW') return 'WINGER';
  if (slot === 'SS' || slot === 'CF') return 'SECOND_STRIKER';
  return 'FINISHER';
}

export interface AppliedRoleBias {
  multiplier: number;
  reasons: string[];
}

export class TacticalRoleSystem {
  definition(role: TacticalRole | null): RoleDefinition | undefined {
    return role ? ROLE_DEFINITIONS[role] : undefined;
  }

  actionBias(
    role: TacticalRole | null,
    action: ActionType,
    passType?: PassType,
    techniqueType?: EngineTechniqueType,
    familiarity = 1,
  ): AppliedRoleBias {
    const definition = this.definition(role);
    if (!definition) return { multiplier: 1, reasons: [] };
    const keys: Array<keyof RoleBiases> = [];
    const resolvedAction = action === 'USE_TECHNIQUE'
      ? techniqueType === 'SHOT' ? 'SHOOT'
        : techniqueType === 'PASS' ? 'PASS'
          : techniqueType === 'DRIBBLE' ? 'DRIBBLE'
            : techniqueType === 'BLOCK' ? 'TACKLE' : action
      : action;
    if (action === 'USE_TECHNIQUE') {
      // Technique bias represents the willingness to spend TP. The embedded
      // action already carries its football score, so multiplying both biases
      // here would compound roles and saturate the utility ceiling.
      keys.push('techniqueBias');
      if (techniqueType === 'BLOCK') keys.push('blockBias');
    } else {
      if (resolvedAction === 'SHOOT') keys.push('shootBias');
      if (resolvedAction === 'PASS') keys.push('passBias');
      if (resolvedAction === 'DRIBBLE' || resolvedAction === 'CARRY') keys.push('dribbleBias');
      if (resolvedAction === 'TACKLE') keys.push('tackleBias');
      if (resolvedAction === 'INTERCEPT') keys.push('interceptBias');
      if (resolvedAction === 'CLEAR_BALL') keys.push('clearBallBias');
      if (resolvedAction === 'PRESS') keys.push('pressBias');
      if (resolvedAction === 'MARK_PLAYER') keys.push('markBias');
      if (resolvedAction === 'SUPPORT') keys.push('supportBias');
      if (resolvedAction === 'HOLD_POSITION') keys.push('holdShapeBias');
      if (resolvedAction === 'MAKE_RUN') keys.push('forwardRunBias');
      if (resolvedAction === 'RECOVER_POSITION') keys.push('recoveryBias');
      if (resolvedAction === 'PASS' && passType === 'THROUGH') keys.push('throughPassBias');
      if (resolvedAction === 'PASS' && (passType === 'CROSS' || passType === 'DRIVEN')) keys.push('widePassBias');
    }

    const boundedFamiliarity = Math.max(0, Math.min(1, familiarity));
    const applied = keys.flatMap((key) => {
      const value = definition.actionBiases[key];
      return value === undefined ? [] : [{ key, value: 1 + (value - 1) * boundedFamiliarity }];
    });
    return {
      multiplier: applied.reduce((value, item) => value * item.value, 1),
      reasons: applied.map((item) => `${definition.id}.${item.key} ×${item.value.toFixed(2)}`),
    };
  }

  movementBias(role: TacticalRole | null, key: keyof RoleBiases): number {
    return this.definition(role)?.movementBiases[key] ?? 1;
  }

  riskBias(role: TacticalRole | null, riskScore: number, familiarity = 1): AppliedRoleBias {
    const tolerance = this.definition(role)?.actionBiases.riskTolerance;
    if (tolerance === undefined) return { multiplier: 1, reasons: [] };
    const boundedFamiliarity = Math.max(0, Math.min(1, familiarity));
    const boundedRisk = Math.max(0, Math.min(1, riskScore));
    // Risk tolerance is applied proportionally: it does not penalize a safe
    // action, and reaches the role's full multiplier only at maximum risk.
    const familiarTolerance = 1 + (tolerance - 1) * boundedFamiliarity;
    const multiplier = 1 + (familiarTolerance - 1) * boundedRisk;
    return {
      multiplier,
      reasons: multiplier === 1 ? [] : [`${role}.riskTolerance ×${multiplier.toFixed(2)}`],
    };
  }
}
